/**
 * Per-body geometry checks (TDD §4.2). Jump-ARC reachability — NOT independent gap/rise
 * thresholds. Every comparison leaves a `clearanceMargin` so exact-contact never passes.
 */
import type { LevelFinding } from '../../types.js';
import type { SpatialLevel } from './schema.js';
import {
  type TraversalGrammar,
  jumpArcHeight,
  arcWidthAboveHeight,
  horizontalDistToHeight,
} from './grammar.js';

export function minPlatform(g: TraversalGrammar): number {
  return Math.max(g.bodyWidth, g.autoRunSpeed * 0.15);
}

export function checkGeometry(
  level: SpatialLevel,
  g: TraversalGrammar,
  runways: number[],
): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const elevById = new Map(level.vertices.map((v) => [v.id, v.elevation]));
  const segments = level.segments ?? [];

  segments.forEach((seg, i) => {
    const at = { vertexId: seg.toId };

    if (seg.kind === 'gap') {
      const dy = (elevById.get(seg.toId) ?? 0) - (elevById.get(seg.fromId) ?? 0);
      const arc = jumpArcHeight(g, seg.span);
      if (dy > arc - g.clearanceMargin) {
        findings.push({
          severity: 'error',
          code: 'ledge-unreachable',
          message: `gap span ${seg.span}m to Δy ${dy}m: jump arc reaches ${arc.toFixed(2)}m, short of Δy + ${g.clearanceMargin}m clearance`,
          at,
        });
      }
    } else if (seg.kind === 'fence') {
      const hf = seg.obstacleHeight ?? 0;
      const w = seg.obstacleWidth ?? 0;
      if (hf > g.maxRise - g.clearanceMargin) {
        findings.push({
          severity: 'error',
          code: 'fence-too-tall',
          message: `fence height ${hf}m exceeds max rise ${g.maxRise}m (minus ${g.clearanceMargin}m clearance)`,
          at,
        });
      } else if (w > arcWidthAboveHeight(g, hf) - g.clearanceMargin) {
        findings.push({
          severity: 'error',
          code: 'fence-too-wide',
          message: `fence width ${w}m exceeds the arc width above ${hf}m (${arcWidthAboveHeight(g, hf).toFixed(2)}m)`,
          at,
        });
      } else if (runways[i] < horizontalDistToHeight(g, hf)) {
        findings.push({
          severity: 'error',
          code: 'fence-insufficient-runway',
          message: `fence needs ${horizontalDistToHeight(g, hf).toFixed(2)}m run-up to reach ${hf}m by its near edge; only ${runways[i].toFixed(2)}m available`,
          at,
        });
      }
    } else if (seg.kind === 'platform' || seg.kind === 'conveyor') {
      if (seg.span < minPlatform(g)) {
        findings.push({
          severity: 'error',
          code: 'platform-too-narrow',
          message: `${seg.kind} span ${seg.span}m below min ${minPlatform(g).toFixed(2)}m`,
          at,
        });
      }
      if (seg.requiresSimultaneous && seg.span < g.minGroupFootprint + g.clearanceMargin) {
        findings.push({
          severity: 'error',
          code: 'platform-too-narrow-for-group',
          message: `simultaneous platform span ${seg.span}m below group footprint ${g.minGroupFootprint}m + ${g.clearanceMargin}m clearance`,
          at,
        });
      }
    }
  });

  return findings;
}

/**
 * Terrace (overhead one-way platform) reachability (§4.2). A terrace is an OPTIONAL
 * alternate high route, so an unusable one is an error only when it is physically
 * unreachable — a surface above the jump apex can never be landed on. A terrace too
 * short to stand on is a warning (it still renders; the author may want a token perch).
 * World width comes from the segment-span walk (the same axis the viewport places on).
 */
export function checkTerraces(level: SpatialLevel, g: TraversalGrammar): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const worldLen = (level.segments ?? []).reduce((sum, s) => sum + Math.max(0, s.span), 0);
  for (const ter of level.terraces ?? []) {
    const at = { terraceId: ter.id };
    if (ter.height > g.maxRise - g.clearanceMargin) {
      findings.push({
        severity: 'error',
        code: 'terrace-too-high',
        message: `terrace height ${ter.height}m exceeds the jump apex ${g.maxRise}m (minus ${g.clearanceMargin}m clearance) — unreachable`,
        at,
      });
    }
    const widthM = Math.max(0, (ter.tEnd - ter.tStart) * worldLen);
    if (widthM > 0 && widthM < g.bodyWidth) {
      findings.push({
        severity: 'warn',
        code: 'terrace-too-narrow',
        message: `terrace width ${widthM.toFixed(2)}m is below body width ${g.bodyWidth}m — too small to stand on`,
        at,
      });
    }
  }
  return findings;
}

const BOSS_INTENT_TAGS = new Set(['boss', 'miniboss', 'mini-boss', 'finale']);

/**
 * Encounter integrity (design §22). A level whose TAGS say "boss"/"miniboss"/"finale" but
 * that carries no `encounter` is the caterpillar bug — the runner auto-runs straight into
 * the boss with no arena/attack pattern. Warn (never block: the level still plays as a run).
 * Also checks each phase's weak point is reachable under the jump apex, else it can't be hit.
 */
export function checkEncounter(
  level: SpatialLevel,
  g: TraversalGrammar,
  tags?: readonly string[],
): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const bossTagged = (tags ?? []).some((t) => BOSS_INTENT_TAGS.has(t.toLowerCase()));
  if (bossTagged && !level.encounter) {
    findings.push({
      severity: 'warn',
      code: 'encounter-missing',
      message:
        'boss/mini-boss-tagged level has no `encounter` — the runner will auto-run into the boss ' +
        'with no arena or attack pattern. Add an encounter (arena + phases) to make it a real fight.',
    });
  }
  if (level.encounter) {
    level.encounter.phases.forEach((p, i) => {
      if (p.weakPointHeight !== undefined && p.weakPointHeight > g.maxRise - g.clearanceMargin) {
        findings.push({
          severity: 'error',
          code: 'encounter-weakpoint-unreachable',
          message: `encounter phase ${i + 1} weak point at ${p.weakPointHeight}m is above the jump apex ${g.maxRise}m — the player can't hit it`,
          at: { encounterPhase: i },
        });
      }
    });
  }
  return findings;
}
